// Per-recipe ingredient checkbox state, stored locally in the browser.
// Keyed by recipe id + ingredient index. Cleared when the user clicks "vyčistit".

import { useEffect, useState } from 'react';

function storageKey(recipeId: number): string {
  return `recipes:checked:${recipeId}`;
}

function read(recipeId: number): Set<number> {
  try {
    const raw = localStorage.getItem(storageKey(recipeId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((n): n is number => Number.isInteger(n)));
  } catch {
    return new Set();
  }
}

function write(recipeId: number, indices: Set<number>): void {
  try {
    localStorage.setItem(storageKey(recipeId), JSON.stringify([...indices]));
  } catch {
    // private mode etc — silently ignore
  }
}

export function useCheckedIngredients(recipeId: number) {
  const [checked, setChecked] = useState<Set<number>>(() => read(recipeId));

  useEffect(() => {
    setChecked(read(recipeId));
  }, [recipeId]);

  const toggle = (index: number) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      write(recipeId, next);
      return next;
    });
  };

  const clear = () => {
    setChecked(new Set());
    write(recipeId, new Set());
  };

  return { checked, toggle, clear };
}
